import React, {useCallback, useMemo, useState} from 'react'
import {Modal} from 'antd'
import useTranslate from 'modules/local/useTranslate'
import {getResponseItem, NullAsDefault} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {RequiredSystemAdmin} from 'components/RequiredAdmin'
import {DelayRender} from 'views/Shared/components/DelayRender'
import LoadingBlock from 'views/Loading/LoadingBlock'
import Async from 'modules/asyncCache/components/Async'
import {BiSolidEdit, BiTrash} from 'react-icons/bi'
import {HiPlus} from 'react-icons/hi2'

// Bộ điều khiển thêm/sửa/xóa dùng CHUNG cho mọi loại trong tab Tra cứu Danh bạ - cấu hình theo
// từng type qua field `crud` trong directoryTypes.js (useCrudFunc/CreateForm/EditForm/
// getByIdApiInfo/...), không hardcode riêng cho loại nào. Nhân bản cho loại mới chỉ cần khai báo
// `crud` trong directoryTypes.js, không cần đụng lại chỗ này hay DiscoveryPage.js.
// Xem docs/ui/design-principles.md "Pattern: Inline CRUD trong list".
export const useDirectoryItemCrud = (crud) => {
    const t = useTranslate()
    const [action, setAction] = useState({})
    const [refreshKey, setRefreshKey] = useState()

    const onCancel = useCallback(() => setAction({}), [])
    const onSuccess = useCallback(() => {
        setAction({})
        setRefreshKey(Date.now())
    }, [])

    const {onCreate, onUpdate, onDelete} = crud?.useCrudFunc?.({
        notifyListeners: {onCreated: onSuccess, onUpdated: onSuccess, onDeleted: onSuccess},
    }) ?? {}

    // item?.edit là cờ quyền backend tính sẵn theo từng dòng - không có quyền thì ẩn HẲN icon menu,
    // không chỉ ẩn nội dung menu (icon rỗng vẫn hiện = rò rỉ UI cho user không có quyền).
    const renderContextMenu = useCallback((item) => {
        if (!crud || !item?.edit) return null
        return createContextMenu(
            [
                {
                    icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                    label: <span className="text-sm text-color-200">{t('edit')}</span>,
                    key: CRUD.UPDATE,
                },
                {
                    icon: <BiTrash size={14} className="text-primary-300"/>,
                    label: <span className="text-sm text-color-200">{t('delete')}</span>,
                    key: CRUD.DELETE,
                },
            ],
            (key) => {
                switch (key) {
                    case CRUD.UPDATE:
                        setAction({type: key, value: item})
                        break
                    case CRUD.DELETE:
                        showDeleteConfirmDialog({
                            translate: t,
                            title: crud.deleteTitle ?? 'delete this item',
                            message: crud.deleteMessage ?? 'are you sure you want to delete this item',
                            onOk: () => onDelete({}, {id: item?.id}),
                        })
                        break
                    default:
                        break
                }
            }
        )
    }, [crud, t, onDelete])

    const openCreate = useCallback(() => setAction({type: CRUD.CREATE}), [])

    const createButton = crud ? (
        <RequiredSystemAdmin>
            <button
                type="button"
                onClick={openCreate}
                style={{width: 40, height: 40, borderRadius: '0.65rem', cursor: 'pointer'}}
                title={t(crud.addLabel)}
                className="flex flex-center transition-all border duration-300 hover:scale-96 bg-primary-50 hover:bg-primary-100 border-primary-400 hover:border-primary text-primary">
                <HiPlus size={18}/>
            </button>
        </RequiredSystemAdmin>
    ) : null

    const modal = useMemo(() => {
        if (!crud || !action.type) return null

        const {CreateForm, EditForm, getByIdApiInfo} = crud
        let Content = NullAsDefault
        let modal_params = {}

        if (action.type === CRUD.CREATE) {
            modal_params = {
                width: crud.createWidth ?? 640,
                title: (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t(crud.createTitle)}
                    </div>
                ),
            }
            Content = () => <CreateForm onCancel={onCancel} onSubmit={onCreate}/>
        } else if (action.type === CRUD.UPDATE) {
            modal_params = {
                width: crud.editWidth ?? 640,
                title: (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t(crud.editTitle)}
                    </div>
                ),
            }
            Content = () => (
                <Async apiInfo={getByIdApiInfo} query={{id: action.value?.id}}>
                    {({response, isLoading}) => isLoading ? <LoadingBlock/> : (
                        <EditForm
                            initialValues={getResponseItem(response) ?? action.value}
                            onCancel={onCancel}
                            onSubmit={(values) => onUpdate(values, {id: action.value?.id})}
                        />
                    )}
                </Async>
            )
        } else {
            return null
        }

        return (
            <Modal
                className="custom-modal"
                footer={null}
                onCancel={onCancel}
                open={!!action.type}
                destroyOnHidden={true}
                {...modal_params}>
                <DelayRender lazy={true}>
                    <Content/>
                </DelayRender>
            </Modal>
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crud, action, onCancel, onCreate, onUpdate, t])

    return {refreshKey, renderContextMenu, createButton, modal}
}
